import React, { useEffect, useRef, useState } from "react";
import { ArrowClockwiseIcon as ArrowClockwise } from "@phosphor-icons/react";

/**
 * Touch-only pull-to-refresh wrapper.
 *
 * Wraps the page body (or any scrollable section) and fires `onRefresh`
 * when the user drags down at the top of the scroll past a threshold,
 * then releases. Matches the iOS Mail / Twitter / Instagram gesture
 * people already know.
 *
 *   <PullToRefresh onRefresh={loadBookings}>
 *     <BookingsList rows={rows} />
 *   </PullToRefresh>
 *
 * Why a custom hook instead of a library:
 *   - No new dep (react-simple-pull-to-refresh is ~6KB gzipped and
 *     has DOM-API quirks on iOS Safari overscroll).
 *   - We can scope it to TOUCH events only — desktop users have F5
 *     and don't need the gesture, and binding it on desktop ends up
 *     stealing trackpad scrolls.
 *   - We get to define the visual exactly (gold arrow, cream pill,
 *     1.5x dampened pull, ~64px threshold) instead of fighting CSS.
 *
 * Behavior:
 *   - Active only when `window.scrollY === 0`. Anywhere else, the
 *     gesture is ignored (regular scroll wins).
 *   - Pull distance is dampened to ~67% so it never feels like the
 *     user is dragging a 1:1 sheet — that's the iOS feel.
 *   - Below threshold on release → snap back, no fire.
 *   - At/above threshold on release → fire onRefresh, hold spinner
 *     until the promise resolves.
 *   - Multitouch and form-input touches are ignored so the gesture
 *     doesn't fire while someone tries to type in a search box.
 */
const THRESHOLD = 72;        // px to fire refresh
const MAX_PULL = 120;        // px max visible pull (after damping)
const DAMP = 0.55;           // resistance factor (lower = harder to pull)

export default function PullToRefresh({ onRefresh, children, disabled = false, testId = "pull-to-refresh" }) {
  const startY = useRef(null);
  const wrapRef = useRef(null);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (disabled) return undefined;
    const isTouch = typeof window !== "undefined" && "ontouchstart" in window;
    if (!isTouch) return undefined; // desktop: feature off
    const el = wrapRef.current;
    if (!el) return undefined;

    const onStart = (e) => {
      if (refreshing) return;
      // Only single-finger drags count as a refresh gesture.
      if (e.touches.length !== 1) return;
      // Don't fire from inside form controls.
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select", "button"].includes(tag)) return;
      // Only when the page is scrolled to the very top.
      if (window.scrollY > 0) return;
      startY.current = e.touches[0].clientY;
    };

    const onMove = (e) => {
      if (refreshing || startY.current == null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) return; // upward swipe → not us
      // Don't preventDefault — passive listener perf matters more
      // than stopping the bounce. The visual still tracks the finger.
      const damped = Math.min(MAX_PULL, dy * DAMP);
      setPull(damped);
    };

    const onEnd = async () => {
      if (refreshing || startY.current == null) return;
      const final = pull;
      startY.current = null;
      if (final >= THRESHOLD && typeof onRefresh === "function") {
        setRefreshing(true);
        setPull(THRESHOLD); // hold the spinner at threshold height
        try {
          await onRefresh();
        } catch {
          /* swallow — the underlying fetch's own error UI handles it */
        }
        setRefreshing(false);
        setPull(0);
      } else {
        setPull(0);
      }
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: true });
    el.addEventListener("touchend", onEnd, { passive: true });
    el.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [onRefresh, disabled, pull, refreshing]);

  // Visual: arrow that rotates as you pull, spinner once firing.
  // Rotation = 0deg at idle → 180deg at threshold (looks like the
  // iOS arrow flipping to indicate "release to refresh").
  const progress = Math.min(1, pull / THRESHOLD);
  const arrowRot = progress * 180;
  const ready = progress >= 1;
  const showIndicator = pull > 8 || refreshing;

  return (
    <div ref={wrapRef} data-testid={testId} className="relative">
      {showIndicator && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-0 right-0 -top-2 z-10 flex justify-center"
          style={{
            transform: `translateY(${pull}px)`,
            transition: refreshing || pull === 0 ? "transform 220ms ease-out" : "none",
          }}
          data-testid={`${testId}-indicator`}
        >
          <div
            className="mt-1 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white border border-gold/40 shadow-md"
            data-testid={`${testId}-spinner`}
          >
            <ArrowClockwise
              size={20}
              weight={ready || refreshing ? "fill" : "bold"}
              className={
                refreshing
                  ? "text-gold-dark animate-spin"
                  : ready
                    ? "text-gold-dark"
                    : "text-[#9A9A9A]"
              }
              style={{
                transform: refreshing ? undefined : `rotate(${arrowRot}deg)`,
                transition: pull === 0 ? "transform 220ms ease-out" : "none",
              }}
            />
          </div>
        </div>
      )}
      <div
        style={{
          transform: refreshing || pull > 0 ? `translateY(${pull}px)` : undefined,
          transition: refreshing || pull === 0 ? "transform 220ms ease-out" : "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}
